import { router } from '@inertiajs/react'
import { Button } from '@/components/ui/button'
import { Heart, ArrowLeft } from 'lucide-react'

interface Founder {
  slug: string
  name: string
  title: string
  bio: string
}

interface FounderProps {
  founder: Founder
}

export default function Founder({ founder }: FounderProps) {
  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 dark:from-gray-950 dark:via-gray-900 dark:to-gray-950">
      {/* Navigation Bar */}
      <nav className="sticky top-0 z-50 border-b bg-white/80 backdrop-blur-sm dark:bg-gray-950/80 dark:border-gray-800">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="flex h-16 items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-gradient-to-br from-blue-600 to-indigo-600">
                <Heart className="h-6 w-6 text-white" />
              </div>
              <span className="text-xl font-semibold">Breakthrough Health</span>
            </div>
            <div className="flex gap-2">
              <Button variant="ghost" onClick={() => router.visit('/about')}>
                <ArrowLeft className="mr-2 h-4 w-4" />
                Back to About
              </Button>
              <Button variant="ghost" onClick={() => router.visit('/')}>
                Home
              </Button>
            </div>
          </div>
        </div>
      </nav>

      {/* Content Section */}
      <section className="mx-auto max-w-4xl px-4 py-20 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h1 className="text-4xl font-bold tracking-tight sm:text-5xl mb-4">
            {founder.name}
          </h1>
          <p className="text-xl text-muted-foreground">{founder.title}</p>
          <div className="h-1 w-24 mx-auto mt-6 bg-gradient-to-r from-blue-600 via-indigo-600 to-purple-600 rounded-full"></div>
        </div>

        <div className="prose prose-lg dark:prose-invert max-w-none">
          <div className="space-y-6 text-lg leading-relaxed text-foreground">
            <div className="p-12 bg-white/50 dark:bg-gray-900/50 backdrop-blur-sm rounded-lg text-center">
              <p className="text-2xl font-light text-muted-foreground italic">
                {founder.bio}
              </p>
            </div>

            <div className="mt-8 p-6 bg-blue-50 dark:bg-blue-950/30 rounded-lg text-center">
              <p className="text-sm text-muted-foreground">
                More information about our founders will be available soon. In the meantime, learn more about our mission on the{' '}
                <button
                  onClick={() => router.visit('/about')}
                  className="text-blue-600 dark:text-blue-400 hover:underline font-semibold"
                >
                  About Us
                </button>
                {' '}page.
              </p>
            </div>
          </div>
        </div>

        {/* CTA Section */}
        <div className="mt-16 text-center flex gap-4 justify-center">
          <Button size="lg" onClick={() => router.visit('/about')}>
            Back to About Us
          </Button>
          <Button size="lg" variant="outline" onClick={() => router.visit('/')}>
            Back to Home
          </Button>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t bg-white/50 backdrop-blur-sm dark:bg-gray-950/50 dark:border-gray-800 mt-20">
        <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
          <div className="flex flex-col items-center gap-4 text-center text-sm text-muted-foreground md:flex-row md:justify-between">
            <p>&copy; 2026 Breakthrough Health. Empowering community health through technology.</p>
            <div className="flex gap-6">
              <button
                onClick={() => router.visit('/about')}
                className="hover:text-foreground transition-colors"
              >
                About Us
              </button>
              <button
                onClick={() => router.visit('/disclaimer')}
                className="hover:text-foreground transition-colors"
              >
                Disclaimer
              </button>
              <button
                onClick={() => router.visit('/privacy')}
                className="hover:text-foreground transition-colors"
              >
                Privacy Policy
              </button>
            </div>
          </div>
        </div>
      </footer>
    </div>
  )
}
